import React, { useState, useMemo } from "react";
import { graphql } from "gatsby";
import PropTypes from "prop-types";
import { ListingPage as PageWrapper } from "../../components/Page";
import { massageGhostVideosData } from "../../gatsbyGhostNoissue";
import { createListFilters } from "../../ng-components/Filter/ListFilters";
import { useTheme } from "../../ng-components/theme";
import { TextFilter } from "../../ng-components/Filter/TextFilter";
import { VideoTypeFilter } from "../../ng-components/Filter/VideoTypeFilter";
import { VideoCommunityFilter } from "../../ng-components/Filter/VideoCommunityFilter";
import { VideoCard } from "../../ng-components/Video/VideoCard";
import {
    createSelectFilter,
    createFilterFn,
} from "../../ng-components/Filter/SelectFilter";
import { CardCarousel } from "../../ng-components/Card/CardCarousel";
import { PaginatedCardGrid } from "../../ng-components/CardGrid";
import { NoResults } from "../../ng-components/Page/Page";
import { SeriesBadgeGroup } from "../../ng-components/SeriesBadgeGroup";
import { VideoDetailsModal } from "../../ng-components/Video/VideoDetails";
import PageBackgroundTop from "../../ng-components/Page/noissuetv-background-top.svg";
import PageBackgroundBottom from "../../ng-components/Page/noissuetv-background-bottom.svg";
import seriesData from '../../utils/seriesData';

const VIDEOS_PER_CHANNEL = 12;
const RELATED_VIDEOS = 6;

const getVideoCards = (videos, onVideoClick) =>
    videos.map((video) => {
        const {
            id,
            title,
            excerpt,
            publishedDate,
            slug,
            url,
            postLink,
        } = video;

        const onClick = (e) => {
            e.stopPropagation();
            return onVideoClick(video);
        };

        return {
            key: id,
            display: (
                <VideoCard
                    key={id}
                    title={title}
                    excerpt={excerpt}
                    slug={slug}
                    postLink={postLink}
                    url={url}
                    publishedDate={publishedDate}
                    onClick={onClick}
                />
            ),
        };
    });

const Page = ({
    data: { videoNodes, page, channelNodes },
    location,
}) => {
    const videos = massageGhostVideosData(videoNodes);
    const theme = {
        pageBackgroundColor: "#FAD6C3",
        pageHeadingColor: "#2748F3",
        pageBackgroundTop: PageBackgroundTop,
        pageBackgroundBottom: PageBackgroundBottom,
        contrastTextColor: "#2748F3",
    };

    const [selectedChannel, setSelectedChannel] = useState({
        value: null,
        filterFn: null,
    });

    const onChannelSelect = (selChannel) =>
        setSelectedChannel({
            value: selChannel,
            filterFn: (item) =>
                [(item) => item.channel.slug].some((field) =>
                    createFilterFn(selChannel)(field(item))
                ),
        });

    const [selectedVideo, setSelectedVideo] = useState(null);
    const getRelatedInfo = (video) => {
        if (video === null) {
            return { channel: {}, videos: [] };
        }

        const { channel: videoChannel, slug: videoSlug } = video;
        // Assume videos already ordered by publishedDate DESC
        return {
            channel: videoChannel,
            videos: videos
                .filter(
                    ({ slug, channel }) =>
                        channel.slug === videoChannel.slug && slug !== videoSlug
                )
                .slice(0, RELATED_VIDEOS),
        };
    };

    const ListFilters = useMemo(
        () =>
            createListFilters({
                primaryFilters: [
                    {
                        key: "text",
                        component: TextFilter,
                        searchFields: [
                            (item) => item.title,
                            (item) => item.excerpt,
                            // TODO: Fetch contentful entry tags
                            // (item) => item.tags.map(({ label }) => label),
                        ],
                    },
                ],
                secondaryFilters: [
                    {
                        key: "videoType",
                        component: VideoTypeFilter,
                        searchFields: [(item) => item.type.id],
                    },
                    {
                        key: "channel",
                        component: createSelectFilter(
                            "Channel",
                            channelNodes.nodes
                        ),
                        searchFields: [(item) => item.channel.slug],
                    },
                    {
                        key: "community",
                        component: VideoCommunityFilter,
                        searchFields: [(item) => item.community?.id],
                    },
                ],
            }),
        [channelNodes.nodes]
    );

    return (
        <PageWrapper theme={theme} data={page} location={location}>
            <ListFilters
                items={videos}
                values={{ channel: selectedChannel }}
                exhibit={<SeriesBadgeGroup seriesList={seriesData} />}
                onChange={({ channel }) => setSelectedChannel(channel)}
                render={(filteredVideos, isFiltersTouched) => {
                    return !isFiltersTouched ? (
                        <DefaultView
                            channels={channelNodes.nodes}
                            videos={filteredVideos}
                            onChannelSelect={onChannelSelect}
                            onOpenVideo={setSelectedVideo}
                        />
                    ) : (
                        <PageContent>
                            <FilteredView
                                videos={filteredVideos}
                                onOpenVideo={setSelectedVideo}
                            />
                        </PageContent>
                    );
                }}
            />
            <VideoDetailsModal
                video={selectedVideo}
                related={getRelatedInfo(selectedVideo)}
                onVideoOpen={setSelectedVideo}
                onClose={() => setSelectedVideo(null)}
            />
        </PageWrapper>
    );
};

function DefaultView({ channels, videos, onChannelSelect, onOpenVideo }) {
    const channelsDisplay = channels.map((channel) => {
        const { label: channelName, key: channelSlug } = channel;
        const channelVideos = videos
            .filter(({ channel }) => channel.slug === channelSlug)
            .slice(0, VIDEOS_PER_CHANNEL);
        const cards = getVideoCards(channelVideos, onOpenVideo);
        return (
            !!cards.length && (
                <CardCarousel
                    className="noissuetv-channels__channel"
                    key={channelSlug}
                    heading={channelName}
                    cards={cards}
                    onViewAll={() => onChannelSelect(channel)}
                />
            )
        );
    });
    return (
        <div className="noissuetv-channels">
            {channelsDisplay}
            <style jsx>{`
                .noissuetv-channels :global(.noissuetv-channels__channel) {
                    padding: 0.5em 0 4em 0;
                }
                .noissuetv-channels
                    :global(.noissuetv-channels__channel:first-child) {
                    padding-top: 2.5em;
                }
                .noissuetv-channels
                    :global(.noissuetv-channels__channel):nth-child(2n) {
                    background-color: #fee8dc;
                }
            `}</style>
        </div>
    );
}

function FilteredView({ videos, onOpenVideo }) {
    return !!videos.length ? (
        <PaginatedCardGrid cards={getVideoCards(videos, onOpenVideo)} />
    ) : (
        <NoResults />
    );
}

function PageContent({ children }) {
    const { contentWidth } = useTheme();
    return (
        <div className="page-content">
            {children}
            <style jsx>{`
                .page-content {
                    padding: 0 1.25em;
                    box-sizing: content-box;
                    max-width: ${contentWidth}em;
                    margin: 2em auto 0;
                }
            `}</style>
        </div>
    );
}

PageContent.propTypes = {
    children: PropTypes.node.isRequired,
};

export default Page;

export const query = graphql`
    query noissuetv {
        page: ghostPage(slug: { eq: "noissuetv" }) {
            ...GhostPageFields
        }
        videoNodes: allContentfulVideo(
            sort: { fields: originalPublishedDate, order: DESC }
            filter: { node_locale: { eq: "en-US" } }
            limit: 1000
            skip: 0
        ) {
            nodes {
                id
                slug
                title
                url
                postSlug
                productSlug
                excerpt {
                    excerpt
                }
                originalPublishedDate
                createdAt
                type {
                    id: contentful_id
                }
                channel {
                    slug
                    name
                }
                community {
                    id: contentful_id
                }
            }
        }
        channelNodes: allContentfulVideoChannel(
            filter: { node_locale: { eq: "en-US" } }
            sort: { fields: order, order: ASC }
        ) {
            nodes {
                label: name
                key: slug
            }
        }
    }
`;
